import { readFileSync } from "node:fs";
import path from "node:path";

/**
 * Detect a running heavy build gate (test/typecheck/build) on a Conveyor pod.
 *
 * `scripts/singleton.sh` records the session-leader pid of the currently
 * running heavy command in `$CONVEYOR_RUN_DIR/<key>.pid` (keys: `heavy` in
 * global scope, or the per-label `test`/`typecheck`/`build` in category
 * scope; `dev` is the always-on dev server and deliberately NOT a gate).
 *
 * The periodic WIP flush consults this to SKIP its tick while a gate runs:
 * a mid-gate flush hashes a tree the gate is actively churning, competes for
 * the same starved disk/CPU, and captures throwaway build artifacts — it is
 * both the slowest and the least useful moment to snapshot. The tree the
 * human cares about doesn't change while the agent is parked waiting on a
 * gate, so deferring to the next tick loses nothing.
 */

const GATE_KEYS = ["heavy", "test", "typecheck", "build"] as const;

function runDir(): string {
  return process.env.CONVEYOR_RUN_DIR ?? "/tmp/conveyor-run";
}

function pidAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch {
    return false;
  }
}

/** True when a singleton-managed heavy gate is currently running on this pod.
 *  Never throws; false on any read failure (off-pod there are no pidfiles). */
export function isHeavyGateActive(): boolean {
  for (const key of GATE_KEYS) {
    try {
      const raw = readFileSync(path.join(runDir(), `${key}.pid`), "utf8").trim();
      const pid = Number.parseInt(raw, 10);
      if (Number.isInteger(pid) && pid > 0 && pidAlive(pid)) return true;
    } catch {
      // no pidfile for this key — not running
    }
  }
  return false;
}
